/* eslint-disable react/jsx-props-no-spreading */
/* eslint-disable react/jsx-no-useless-fragment */
import React, { useEffect, useState } from "react";
import { Box, Typography } from "@material-ui/core";
import {
  B2BPaymentMethodSelectWorkflow,
  B2BSpinner,
  CheckoutPaymentForm,
  GenericInfoPopup,
  Icon,
  IconName,
  LoadingIndicator,
  LoadingPopup,
  PoweredByHopper,
  TEST_CARD_LAST_FOURS,
  useDeviceTypes,
  paymentTextConstants,
  PrefillPaymentFormData,
  SanitizedHtml,
} from "halifax";
import clsx from "clsx";
import { RouteComponentProps } from "react-router-dom";
import { CallState, TokenizeCardErrors } from "redmond";
import { isVesperTenant, getConfigTenant } from "@capone/common";
import { PaymentMethodsSectionConnectorProps } from "./container";
import { config } from "../../../../api/config";
import "./styles.scss";

export interface PaymentMethodsSectionProps
  extends PaymentMethodsSectionConnectorProps,
    RouteComponentProps {}

export const PaymentMethodsSection = (props: PaymentMethodsSectionProps) => {
  const {
    rewardsAccounts,
    paymentMethods,
    deletePaymentMethod,
    listPaymentMethodCallState,
    deletePaymentMethodCallState,
    verifyPaymentMethodCallState,
    verifyPaymentMethod,
    verifyPaymentMethodResults,
    fetchRewardsAccountCallState,
  } = props;
  const { matchesMobile } = useDeviceTypes();
  const [openErrorPaymentModal, setOpenErrorPaymentModal] = useState(false);
  const [isNotCapOneAccount, setIsNotCapOneAccount] = useState(false);
  const [tokenizeErrors, setTokenizeErrors] = useState<TokenizeCardErrors[]>(
    [],
  );
  const [paymentFormPrefillData, setPaymentFormPrefillData] =
    useState<PrefillPaymentFormData | null>(null);

  const isVesper = isVesperTenant(getConfigTenant());

  const handleOnAddPaymentMethod = (token: string, last4: string) => {
    // note: cap1 specific logic:
    // A card should be deemed ineligible if the last four digits do not match one of the cards associated with their accounts.
    const account = rewardsAccounts.find(
      (account) =>
        account.lastFour === last4 ||
        account.lastFourVirtualCardNumbers?.includes(last4) ||
        account.discoverNetworkFlipInfo?.transferFromCardNumberLastFour ===
          last4,
    );
    let matchingRewardsAccount = account;

    const isAddingVCNPaymentMethod = !!(
      matchingRewardsAccount?.lastFourVirtualCardNumbers &&
      matchingRewardsAccount?.lastFourVirtualCardNumbers?.includes(last4)
    );

    // TODO: bad practice, remove this in favor of real test accounts when we have them
    const isTestCard =
      window.__mclean_env__.ENV !== "production" &&
      TEST_CARD_LAST_FOURS.includes(last4);
    if (isTestCard) {
      matchingRewardsAccount = rewardsAccounts[0];
    }

    if (!!matchingRewardsAccount || isTestCard) {
      verifyPaymentMethod(
        { token },
        matchingRewardsAccount?.accountReferenceId!,
        isAddingVCNPaymentMethod,
      );
    } else {
      setIsNotCapOneAccount(true);
      setOpenErrorPaymentModal(true);
    }
  };

  const handleCloseErrorPopup = () => {
    isNotCapOneAccount && setIsNotCapOneAccount(false);
    setOpenErrorPaymentModal(false);
  };

  useEffect(() => {
    if (verifyPaymentMethodCallState === CallState.Failed) {
      setOpenErrorPaymentModal(true);
    }
  }, [verifyPaymentMethodCallState]);

  const PaymentAddingElement = () => (
    <LoadingPopup
      indicatorSize="small"
      indicator={B2BSpinner}
      open
      popupSize="small"
      message="Adding your payment method"
      footer={PoweredByHopper}
    />
  );

  const renderCheckoutPaymentForm = (isOpen: boolean) => {
    const isLoading = verifyPaymentMethodCallState === CallState.InProcess;
    return (
      <CheckoutPaymentForm
        loading={isLoading}
        loadingEl={<PaymentAddingElement />}
        onSubmit={(token, last4, prefillData) => {
          handleOnAddPaymentMethod(token, last4);
          setPaymentFormPrefillData(prefillData);
        }}
        saveLabel="Save"
        onError={(errors) => {
          setTokenizeErrors(errors);
          setOpenErrorPaymentModal(true);
        }}
        spreedlyEnvironmentKey={config.spreedlyEnvironmentKey}
        isMobile={matchesMobile}
        className="b2b"
        tenant={config.TENANT}
        prefillData={!isLoading ? paymentFormPrefillData : null}
        isOpen={isOpen}
      />
    );
  };

  const paymentMethodWorkflowTitles = () => ({
    addPaymentCta: "Add payment method",
    addPaymentModalTitle: paymentTextConstants.ADD_PAYMENT_METHOD_MODAL_TITLE,
    addAdditionalPaymentCta:
      paymentTextConstants.ADD_ADDITIONAL_PAYMENT_METHOD_CTA,
    backToCardSelectionCta: paymentTextConstants.BACK_TO_CARD_SELECTION_CTA,
    paymentFormHeader: (cardName: string) => (
      <SanitizedHtml
        as={Typography}
        variant={matchesMobile ? "h2" : "h6"}
        html={paymentTextConstants.ADD_PAYMENT_FORM_HEADER_TEXT(cardName)}
      />
    ),

    cardEndingIn: paymentTextConstants.CARD_ENDING_IN_TEXT,
    noCardAddedTitle:
      paymentMethods.length === 0
        ? "You haven't added a payment method yet."
        : undefined,
    usePlusIconInCTA: false,
    showAddPaymentIcon: isVesper ? false : matchesMobile,
  });

  return (
    <>
      {listPaymentMethodCallState === CallState.InProcess ||
      deletePaymentMethodCallState === CallState.InProcess ||
      fetchRewardsAccountCallState === CallState.InProcess ? (
        <LoadingIndicator
          indicatorSize="small"
          indicator={B2BSpinner}
          message={
            listPaymentMethodCallState === CallState.InProcess ||
            fetchRewardsAccountCallState === CallState.InProcess
              ? `Fetching`
              : `Deleting`
          }
        />
      ) : (
        <Box
          className={clsx("payment-methods-section", {
            mobile: matchesMobile,
            redesigned: isVesper,
          })}
        >
          <B2BPaymentMethodSelectWorkflow
            rewardsAccounts={rewardsAccounts ?? []}
            savedPayments={paymentMethods ?? []}
            disabled={openErrorPaymentModal}
            removePaymentMethod={(paymentId: string) => {
              deletePaymentMethod({ paymentId });
            }}
            titles={paymentMethodWorkflowTitles()}
            renderCheckoutPaymentForm={renderCheckoutPaymentForm}
            isMobile={matchesMobile}
            loading={verifyPaymentMethodCallState === CallState.InProcess}
            buttonClassName="b2b"
            fullScreenWithBanner={matchesMobile}
            paymentMethodDisabled={(_r) => false}
            showEarnValue={false}
            isVCNEnabled={false}
            addPaymentMethodIcon={
              <Icon name={IconName.AddPaymentCardFilledIcon} />
            }
          />
          <GenericInfoPopup
            open={openErrorPaymentModal}
            image={
              <Icon
                className="error-icon"
                name={
                  isNotCapOneAccount
                    ? IconName.ErrorState
                    : IconName.UnableToProcess
                }
              />
            }
            {...(verifyPaymentMethodResults &&
            verifyPaymentMethodCallState === CallState.Failed
              ? {
                  ...paymentTextConstants.PAYMENT_METHOD_ERROR_TITLES(
                    verifyPaymentMethodResults,
                    rewardsAccounts[0],
                    false,
                  ),
                  buttons: [
                    {
                      buttonText: paymentTextConstants.EDIT_PAYMENT_METHOD,
                      onClick: () => {
                        handleCloseErrorPopup();
                      },
                      defaultStyle: "h4r-primary",
                    },
                  ],
                }
              : {
                  ...{
                    title: isNotCapOneAccount
                      ? paymentTextConstants.CAP_ONE_INVALID_CREDIT_CARD_TITLE
                      : paymentTextConstants.UNABLED_TO_ADD_PAYMENT,
                    subtitle: isNotCapOneAccount
                      ? paymentTextConstants.CAP_ONE_INVALID_CREDIT_CARD_SUBTITLE(
                          config.TENANT,
                        )
                      : tokenizeErrors.length > 0
                      ? tokenizeErrors[0].message
                      : paymentTextConstants.ADD_PAYMENT_AGAIN,
                    buttons: [
                      {
                        buttonText: paymentTextConstants.EDIT_PAYMENT_METHOD,
                        onClick: () => {
                          handleCloseErrorPopup();
                        },
                        defaultStyle: "h4r-secondary",
                      },
                      {
                        buttonText: paymentTextConstants.TRY_AGAIN,
                        onClick: () => {
                          handleCloseErrorPopup();
                        },
                        defaultStyle: "h4r-primary",
                        buttonWrapperClassName: "b2b",
                      },
                    ],
                  },
                })}
            isMobile={matchesMobile}
            className="payment-section-error-modal"
          />
        </Box>
      )}
    </>
  );
};
